import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MatchItem from '../components/MatchItem';
import TeamCrest from '../components/TeamCrest';
import Seo from '../components/Seo';
import { organizationSchema, websiteSchema } from '../utils/structuredData';
import { formatDate, format12HourTime } from '../utils/formatters';
import { matchStart, countdownTo, firstStreamUrl } from '../utils/match';
import '../styles/HomePage.css';

const HomePage = () => {
  const [upcoming, setUpcoming] = useState([]);
  const [results, setResults] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(true);
  const [loadingResults, setLoadingResults] = useState(true);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const load = async () => {
      try {
        const [up, done] = await Promise.all([
          fetch('/api/rl/public/upcoming-matches').then(r => r.json()),
          fetch('/api/rl/public/completed-matches').then(r => r.json()),
        ]);
        setUpcoming(Array.isArray(up) ? up : []);
        setResults(Array.isArray(done) ? done : []);
      } catch (error) {
        console.error('Error loading matches:', error);
      } finally {
        setLoadingUpcoming(false);
        setLoadingResults(false);
      }
    };
    load();
  }, []);

  // The hero countdown only needs minute resolution.
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(tick);
  }, []);

  const next      = upcoming[0];
  const nextStart = matchStart(next);
  const watchUrl  = firstStreamUrl(next?.stream_links);
  const nextTeam1 = next?.team_1_name || 'TBD';
  const nextTeam2 = next?.team_2_name || 'TBD';

  return (
    <>
      <Seo path="/" schemas={[organizationSchema(), websiteSchema()]} />

      <section className="hero">
        <div className="hero-copy">
          <span className="hero-brand">JoltStats</span>
          <h1>Every Rocket League match, tracked.</h1>
          <p>Schedules, live scores and results across RLCS, regionals and majors.</p>
        </div>

        {next ? (
          <article className="next-match">
            <div className="next-head">
              <span className="next-label">Next match</span>
              <span className="next-countdown tabular">{countdownTo(nextStart, now)}</span>
            </div>

            <Link to={`/match/${next.matchid}/${next.date}`} className="next-teams">
              <span className="next-team">
                <TeamCrest name={nextTeam1} size="lg" />
                <span className="next-team-name">{nextTeam1}</span>
              </span>
              <span className="next-vs">vs</span>
              <span className="next-team">
                <TeamCrest name={nextTeam2} size="lg" />
                <span className="next-team-name">{nextTeam2}</span>
              </span>
            </Link>

            <div className="next-foot">
              <span className="next-when tabular">
                {formatDate(next.date)}
                {next.time ? ` · ${format12HourTime(next.time)}` : ''}
              </span>
              {next.match_format?.count ? (
                <span className="match-format">BO{next.match_format.count}</span>
              ) : null}
              {watchUrl && (
                <a
                  className="btn btn-accent next-watch"
                  href={watchUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Watch
                </a>
              )}
            </div>

            {next.league && <p className="next-league">{next.league}</p>}
          </article>
        ) : (
          !loadingUpcoming && (
            <article className="next-match next-match-empty">
              <span className="next-label">Next match</span>
              <p>Nothing scheduled yet. New fixtures land here as soon as they're announced.</p>
            </article>
          )
        )}
      </section>

      <section className="home-section">
        <h2 className="section-title">Upcoming</h2>
        {loadingUpcoming ? (
          <div className="spinner" />
        ) : upcoming.length === 0 ? (
          <div className="empty-state">
            <h3>No matches scheduled</h3>
            <p>Check back when the next event is announced.</p>
          </div>
        ) : (
          <>
            <div className="matches-container">
              {upcoming.slice(0, 4).map(match => (
                <MatchItem key={match.matchid} match={match} date={match.date} />
              ))}
            </div>
            <div className="view-more">
              <Link to="/upcoming-matches" className="view-all-link">All upcoming matches →</Link>
            </div>
          </>
        )}
      </section>

      <section className="home-section">
        <h2 className="section-title">Recent results</h2>
        {loadingResults ? (
          <div className="spinner" />
        ) : results.length === 0 ? (
          <div className="empty-state">
            <h3>No results yet</h3>
            <p>Finished matches appear here with full game-by-game scores.</p>
          </div>
        ) : (
          <>
            <div className="matches-container">
              {results.slice(0, 4).map(match => (
                <MatchItem key={match.matchid} match={match} isCompleted date={match.date} />
              ))}
            </div>
            <div className="view-more">
              <Link to="/completed-matches" className="view-all-link">All results →</Link>
            </div>
          </>
        )}
      </section>
    </>
  );
};

export default HomePage;
